import React, { useState, useRef, useCallback, useEffect } from "react";
import {
  Box,
  Button,
  Typography,
  TextField,
  ToggleButtonGroup,
  ToggleButton,
  Chip,
  Grid,
  IconButton,
  MenuItem,
  Card,
  CardContent,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CameraAltIcon from "@mui/icons-material/CameraAlt";
import axios from "axios";
import { useSelector } from "react-redux";
import ReusableDialog from "../../../../common/dialog/dialog";
import PhotoCameraIcon from "@mui/icons-material/PhotoCamera";
import ImageIcon from "@mui/icons-material/Image";
import Webcam from "react-webcam";

const WeeklyVehicleInspect = ({ onClose, setOpenDefaultSnackbar, selectedTask }) => {
  const questions = [
    "Is the vehicle clean and tidy inside and out?",
    "Is the engine oil full?",
    "Is the break fluid full?",
    "Is the engine coolant full?",
    "Is the Windscreen Washer fluid full?",
    "Is the power steering fluid full?",
    "Are the windscreen wipers in good working order?",
    "Is the tyre tread above the legal limit (1.6mm) (including spare)?",
    "Is the tyre pressure set at the recommended level (including spare)?",
  ];

  const user = useSelector((state) => state.auth.user);
  const today = new Date();
  const currentDate = today.toISOString().split("T")[0];
  const [records, setRecords] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [responses, setResponses] = useState([]);
  const [condition, setCondition] = useState(null);
  const [comments, setComments] = useState("");
  const [attachment, setAttachment] = useState(null);
  const [imageSrc, setImageSrc] = useState(null);
  const fileInputRef = useRef(null);
  const webcamRef = useRef(null);
  const [damageType, setDamageType] = useState("");
  const [selectedAction, setSelectedAction] = useState("");
  const [selectedDate, setSelectedDate] = useState(currentDate);
  const [isCameraOpen, setIsCameraOpen] = useState(false);
  const [taskId, setTaskId] = useState(selectedTask?.id);

  useEffect(() => {
    if (!taskId && selectedTask?.id) {
      setTaskId(selectedTask.id);
    }
  }, [selectedTask?.id]); // Run only if `selectedTask.id` is not set

  const toggleResponse = (index) => {
    const updatedResponses = [...responses];
    updatedResponses[index] = !updatedResponses[index];
    setResponses(updatedResponses);
  };

  const handleDamageTypeChange = (event, newType) => {
    if (newType !== null) {
      setDamageType(newType);
    }
  };

  const handleFileUpload = (event) => {
    setAttachment(event.target.files[0]);
  };

  // Handle file selection
  // const handleFileUpload = (event) => {
  //   const file = event.target.files[0];
  //   if (file) {
  //     const reader = new FileReader();
  //     reader.onload = (e) => setImageSrc(e.target.result);
  //     reader.readAsDataURL(file);
  //     setIsCameraOpen(false); // Close camera if file is selected
  //   }
  // };

  // Handle camera capture
  const capture = useCallback(() => {
    if (webcamRef.current) {
      const image = webcamRef.current.getScreenshot();
      setImageSrc(image);
      setIsCameraOpen(false); // Close the camera after capturing
    }
  }, []);

  const handleSubmitDamage = () => {
    if (!damageType || !comments) {
      alert("Please fill out all fields before submitting.");
      return;
    }

    // Create new damage record
    const newRecord = {
      time: new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }),
      date: currentDate,
      damageType: damageType,
      comments: comments,
      attachment: attachment ? attachment.name : imageSrc,
    };

    // Update the records state with the new damage entry
    setRecords([...records, newRecord]);

    // Clear form inputs and close dialog
    setDamageType("");
    setComments("");
    setAttachment(null);
    setImageSrc(null);
    setDialogOpen(false);
  };

  const handleConditionChange = (event, newCondition) => {
    setCondition(newCondition);
  };

  const handleNext = () => {
    setResponses([
      ...responses,
      {
        question: questions[currentQuestionIndex],
        condition,
        comments,
        damageType,
        date: selectedDate,
      },
    ]);

    setCondition(null);
    setComments("");
    setAttachment(null);
    setImageSrc(null);
    setDamageType("");

    if (currentQuestionIndex < questions.length - 1) {
      setCurrentQuestionIndex((prev) => prev + 1);
    } else {
      handleSubmit();
    }
  };
  const updateSubTask = async (taskId) => {
    if (!taskId) {
      console.error("Task ID is undefined. Cannot update.");
      return;
    }

    try {
      await axios.put(
        `/api/assessments/updateTask/${taskId}`,
        {
          status: "completed",
          checklist: [],
          updated_by: user?.username || "Unknown",
        },
        {
          headers: {
            "Content-Type": "application/json",
            "x-user-role": user?.roleid || "",
          },
        }
      );
      setOpenDefaultSnackbar(true);
    } catch (error) {
      console.error("Error updating subtask:", error);
    }
  };

  const handleSubmit = async () => {
    try {
      // Prepare inspection details list (condition and selectedAction)
      const inspectionDetails = responses.map((response) => ({
        question: response.question,
        condition: response.condition,
        action: selectedAction || "No action",
      }));

      // Prepare damage records list
      const damageRecords = records.map((record) => ({
        time: record.time,
        date: record.date,
        damageType: record.damageType,
        comments: record.comments,
        attachment: record.attachment,
      }));

      // Construct the payload with separate lists
      const payload = {
        inspectionDetails,
        damageRecords,
        completed_by: user.username, // Include the completed_by field
      };

      await axios.post("/api/vehicle/vehicleInspection", payload);

      if (taskId) {
        updateSubTask(taskId); // Use the stored task ID
      }

      setResponses([]);
      setRecords([]);
      setCurrentQuestionIndex(0);
      setCondition(null);
      setSelectedAction("");
      setComments("");
      setAttachment(null);
      setImageSrc(null);
      setDamageType("");
      setSelectedDate(currentDate);
      onClose();
    } catch (error) {
      console.error("Error submitting responses:", error);
      alert("Failed to submit responses. Please try again.");
    }
  };

  return (
    <Box sx={{ p: 4, m: 2 }}>
      <Chip
        variant='contained'
        sx={{ fontSize: "14px", backgroundColor: "#F2F6FF", color: "#1751D0" }}
        label='Weekly Tasks'
      />

      {/* Header */}
      <Typography sx={{ fontSize: "24px", color: "black", fontWeight: "bold", mb: 1 }}>Weekly Vehicle Check</Typography>

      <Typography variant='subtitle2' sx={{ color: "#757575", mb: 1 }}>
        Date: {currentDate} &nbsp; • &nbsp; <span style={{ color: "#1751D0" }}>{user.username}</span>
      </Typography>

      <Typography variant='subtitle2' sx={{ color: "#757575", fontSize: "14px", mb: 2 }}>
        <Grid container>
          <Grid item xs={5}>
            Questions
          </Grid>
          <Grid item xs={4}>
            <span style={{ color: "#1751D0" }}>{currentQuestionIndex + 1} </span>
            {`/ ${questions.length}`}
          </Grid>
        </Grid>
      </Typography>

      <Grid container spacing={3}>
        <Grid item xs={6} md={6}>
          <Grid container spacing={2}>
            {questions.map((question, index) => (
              <Grid
                item
                xs={12}
                key={index}
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  borderBottom: "1px solid #E0E0E0",
                  py: 1,
                }}
              >
                <Typography sx={{ flex: 1, color: "black", fontSize: "14px" }}>{question}</Typography>
                <IconButton
                  color='primary'
                  onClick={() => toggleResponse(index)}
                  sx={{ color: responses[index] + 1 ? "#1751D0" : "#E0E0E0" }}
                >
                  <CheckCircleIcon />
                </IconButton>
              </Grid>
            ))}
          </Grid>
        </Grid>

        <Grid item xs={6} md={6}>
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12}>
              <Grid container>
                <Grid item xs={10}>
                  <Typography sx={{ fontSize: "14px", color: "black" }}>{questions[currentQuestionIndex]}</Typography>
                </Grid>
                <Grid item xs={2}>
                  <ToggleButtonGroup value={condition} exclusive onChange={handleConditionChange}>
                    <ToggleButton value='Yes' sx={{ textTransform: "none" }} color='primary'>
                      Yes
                    </ToggleButton>
                    <ToggleButton value='No' sx={{ textTransform: "none" }} color='error'>
                      No
                    </ToggleButton>
                  </ToggleButtonGroup>
                </Grid>
              </Grid>
            </Grid>
          </Grid>

          <TextField
            fullWidth
            select
            label='Select Action From Here'
            value={selectedAction}
            onChange={(e) => setSelectedAction(e.target.value)}
            sx={{ mt: 2 }}
            disabled={condition === "Yes"}
          >
            <MenuItem value='Inform to the manager'>Inform to the manager</MenuItem>
            <MenuItem value='Refill'>Refill</MenuItem>
          </TextField>

          <Typography variant='h6' sx={{ mt: 3, fontWeight: "bold" }}>
            Vehicle Damage Report
          </Typography>

          <Box sx={{ mt: 2, mb: 2 }}>
            {/* Table Header */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                backgroundColor: "#E3F2FD",
                padding: "8px 16px",
                borderRadius: "8px",
              }}
            >
              <Grid container alignItems='center' spacing={2}>
                <Grid item xs={2}>
                  <Typography sx={{ fontSize: "14px", color: "blue" }}>Time</Typography>
                </Grid>
                <Grid item xs={2}>
                  <Typography sx={{ fontSize: "14px", color: "blue" }}>Date</Typography>
                </Grid>
                <Grid item xs={3}>
                  <Typography sx={{ fontSize: "14px", color: "blue" }}>Damage Category</Typography>
                </Grid>
                <Grid item xs={3}>
                  <Typography sx={{ fontSize: "14px", color: "blue" }}>Status</Typography>
                </Grid>
                <Grid item xs={2}>
                  <Typography sx={{ fontSize: "14px", color: "blue" }}>Attachment</Typography>
                </Grid>
              </Grid>
            </Box>

            {/* Table Content */}
            {condition === "No" &&
              records.map((record, index) => (
                <Card key={index} sx={{ mt: 1, boxShadow: 2 }}>
                  <CardContent>
                    <Grid container alignItems='center' spacing={2}>
                      <Grid item xs={2}>
                        <Typography sx={{ fontSize: "14px", color: "black" }}>{record.time}</Typography>
                      </Grid>
                      <Grid item xs={2}>
                        <Typography sx={{ fontSize: "14px", color: "black" }}>{record.date}</Typography>
                      </Grid>
                      <Grid item xs={3}>
                        <Typography sx={{ fontSize: "14px", color: "black" }}>{record.damageType}</Typography>
                      </Grid>
                      <Grid item xs={3}>
                        <Typography
                          sx={{
                            fontSize: "14px",
                            color: "black",
                            wordBreak: "break-word",
                            whiteSpace: "normal",
                            overflowWrap: "break-word",
                          }}
                        >
                          {record.comments}
                        </Typography>
                      </Grid>
                      <Grid item xs={2} textAlign='center'>
                        <IconButton color='primary'>
                          <CameraAltIcon />
                        </IconButton>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              ))}
          </Box>
          <Button color='primary' disabled={condition !== "No"} onClick={() => setDialogOpen(true)}>
            <AddIcon /> Add another record
          </Button>
        </Grid>
      </Grid>
      <ReusableDialog
        isOpen={dialogOpen}
        onClose={() => setDialogOpen(false)}
        title='Report Damage'
        onSubmit={handleSubmitDamage}
        submitText='Submit'
        cancelText='Cancel'
      >
        <Box sx={{ mb: 2 }}>
          <ToggleButtonGroup value={damageType} exclusive onChange={handleDamageTypeChange}>
            <ToggleButton value='external' sx={{ textTransform: "none" }} color='primary'>
              External Damage
            </ToggleButton>
            <ToggleButton value='interior' sx={{ textTransform: "none" }} color='primary'>
              Interior Damage
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>

        <Typography sx={{ fontSize: "14px", mb: 1 }}>Comment:</Typography>
        <TextField
          fullWidth
          multiline
          rows={3}
          value={comments}
          onChange={(e) => setComments(e.target.value)}
          variant='outlined'
          sx={{ mb: 3 }}
        />

        <Typography sx={{ fontSize: "14px", mb: 1 }}>If any Attachment:</Typography>
        <Box
          sx={{
            border: "2px dashed #ccc",
            padding: 3,
            textAlign: "center",
            cursor: "pointer",
          }}
        >
          {/* Hidden File Input */}
          <input
            type='file'
            accept='image/*'
            ref={fileInputRef}
            onChange={handleFileUpload}
            style={{ display: "none" }}
          />

          {/* Icons for Upload and Camera */}
          <IconButton onClick={() => fileInputRef.current.click()}>
            <ImageIcon sx={{ fontSize: 40, color: "#757575" }} />
          </IconButton>

          <IconButton onClick={() => setIsCameraOpen(true)}>
            <PhotoCameraIcon sx={{ fontSize: 40, color: "#757575" }} />
          </IconButton>

          {/* Camera Capture */}
          {isCameraOpen && (
            <Box sx={{ mt: 2, display: "flex", flexDirection: "column", alignItems: "center" }}>
              <Webcam
                ref={webcamRef}
                screenshotFormat='image/png'
                videoConstraints={{
                  width: 300, // Adjust width
                  height: 200, // Adjust height
                  facingMode: "user", // Front camera
                }}
                style={{
                  width: 300, // Adjust width
                  height: 200, // Adjust height
                  borderRadius: "8px", // Optional: rounded corners
                  border: "2px solid #ccc", // Optional: border
                }}
              />
              <IconButton onClick={capture}>
                <PhotoCameraIcon sx={{ fontSize: 30, color: "#ff5722", mt: 1 }} />
              </IconButton>
            </Box>
          )}

          {/* Display the Uploaded or Captured Image */}
          {imageSrc ? (
            <>
              <br />
              <img
                src={imageSrc}
                alt='Selected or Captured'
                style={{
                  width: 300, // Adjust width
                  height: 200, // Adjust height
                  borderRadius: "8px", // Optional: rounded corners
                  border: "2px solid #ccc", // Optional: border
                }}
              />
            </>
          ) : (
            <Typography sx={{ color: "#757575", marginTop: 1 }}>Select an image or capture a photo</Typography>
          )}
        </Box>
      </ReusableDialog>
      {/* Action Buttons */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "flex-end",
          gap: 2,
          position: "sticky",
          bottom: 0,
          background: "#fff",
          padding: 2,
          zIndex: 10,
        }}
      >
        {/* Cancel Button */}
        {currentQuestionIndex === 0 && (
          <Button variant='outlined' color='primary' onClick={() => onClose()}>
            Cancel
          </Button>
        )}
        {/* Back Button */}
        {currentQuestionIndex > 0 && (
          <Button variant='outlined' color='primary' onClick={() => setCurrentQuestionIndex((prev) => prev - 1)}>
            Previous
          </Button>
        )}
        {/* Next/Submit Button */}
        {currentQuestionIndex < questions.length - 1 ? (
          <Button variant='contained' color='primary' onClick={handleNext} disabled={!condition}>
            Next
          </Button>
        ) : (
          <Button variant='contained' color='primary' onClick={handleSubmit} disabled={!condition}>
            Submit
          </Button>
        )}
      </Box>
    </Box>
  );
};

export default WeeklyVehicleInspect;
